// @flow

import React, { Component } from 'react';
import proj4 from 'proj4';
import AutoComplete from '../form/autocomplete';
import InputAdornment from 'app/common/form/Adornment';
import { fetchAddress } from './fetch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

proj4.defs(
  'EPSG:25832',
  '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs'
);

function sanitizeUnit(data) {
  if (!data || data.status !== 'ok' || !data.adresse) {
    return null;
  }

  const {
    id_lokalid: id,
    adressebetegnelse: address,
    doerbetegnelse: door,
    etagebetegnelse: floor,
    husnummer
  } = data.adresse;

  if (!husnummer) {
    return null;
  }

  const {
    husnummertekst: house,
    vejnavn: street,
    adgangspunkt,
    postnummer
  } = husnummer;

  const rawCoordinates =
    adgangspunkt &&
    adgangspunkt.geometri &&
    adgangspunkt.geometri.coordinates;

  const coordinates = rawCoordinates
    ? proj4('EPSG:25832', 'EPSG:4326', rawCoordinates)
    : null;

  return {
    id,
    href: `https://adressevaelger.dk/adresser/${id}`,
    address,
    street,
    house,
    floor,
    door,
    zip: postnummer ? postnummer.postnr : null,
    city: postnummer ? postnummer.navn : null,
    geo: coordinates ? { coordinates } : null
  };
}

type Props = {
  anonymous: boolean,
  onAnonymousSelect: (value: boolean) => void
} & any;

type State = {
  anonymous: boolean
};

export default class AutoCompleteDAWA extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      anonymous: props.anonymous || false
    };
  }

  handleAnonymousChange = ev => {
    this.setState({ anonymous: ev.target.checked }, () => {
      this.props.onAnonymousSelect('LOCATION');
    });
  };

  getSuggestions = searchText => {
    return searchText.length > 2 ? fetchAddress(searchText) : [];
  };

  handleSelect = (
    event,
    { suggestion /*, suggestionValue, suggestionIndex, sectionIndex, method*/ }
  ) => {
    if (!suggestion || typeof suggestion !== 'object' || !suggestion.value) {
      this._selected = { suggestion };
      return this.props.onSelect(suggestion, null);
    }

    this._selected = { suggestion, pending: true };

    fetch(suggestion.value)
      .then(response => response.json())
      .then(data => sanitizeUnit(data))
      .then(data => {
        if (!data) {
          return;
        }

        this._selected = { suggestion, data };
        this.props.onSelect(suggestion, data);
      });
  };

  render() {
    return (
      <>
        <AutoComplete
          defaultValue={this.props.defaultValue}
          getSuggestions={this.getSuggestions}
          onSuggestionSelected={this.handleSelect}
          focusInputOnSuggestionClick={false}
          error={this.props.error}
          inputProps={{
            onBlur: this.props.onBlur,
            disabled: this.props.locked,
            required: true,
            'data-cy': 'address',
            placeholder: this.props.placeholder,
            label: this.props.label,
            style: {
              marginBottom: 24,
              ...(this.props.style || {}),
              zIndex: 1
            },
            InputLabelProps: { shrink: true },
            startAdornment: (
              <InputAdornment
                icon={
                  this.state.anonymous ? 'LocationOffOutlined' : 'PlaceOutlined'
                }
              />
            ),
            helperText: this.props.onAnonymousSelect && (
              <FormControlLabel
                style={{ marginLeft: '-5px' }}
                control={
                  <Checkbox
                    style={{ padding: '6px' }}
                    color="primary"
                    checked={this.state.anonymous}
                    name="anonymous"
                    onClick={this.handleAnonymousChange}
                  />
                }
                label={'Skjul den fulde adresse i annoncen'}
              />
            )
          }}
        />
      </>
    );
  }
}